import { Decimal } from '@mlm/shared';
import type { KpiCardData } from '../../lib/api';

/**
 * Dashboard number formatting. Cash is pesos (the platform is single-currency
 * PHP; coins/GC have their own labels elsewhere). Money math (net, ratio) goes
 * through Decimal so we never float-add currency; the Intl calls only format
 * an already-aggregated display value.
 */
const peso = new Intl.NumberFormat('en-PH', {
  style: 'currency',
  currency: 'PHP',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const count = new Intl.NumberFormat('en-PH');

/** Format a money string as pesos (₱). Returns the raw input if unparseable. */
export function formatPeso(value: string): string {
  const n = Number(value);
  return Number.isNaN(n) ? value : peso.format(n);
}

/** Format a count string with thousands separators. */
export function formatCount(value: string): string {
  const n = Number(value);
  return Number.isNaN(n) ? value : count.format(n);
}

/** sales − commissions, as a 2dp money string. Decimal-safe. */
export function retainedAfter(sales: string, commissions: string): string {
  try {
    return new Decimal(sales || 0).minus(commissions || 0).toFixed(2);
  } catch {
    return '0.00';
  }
}

/** Commissions as a whole-percent of sales, e.g. "32%". null when sales ≤ 0. */
export function commissionRate(sales: string, commissions: string): string | null {
  try {
    const s = new Decimal(sales || 0);
    if (s.lte(0)) return null;
    return `${new Decimal(commissions || 0).div(s).times(100).toFixed(0)}%`;
  } catch {
    return null;
  }
}

/**
 * May these two figures be combined into a DERIVED readout (a ratio, a
 * difference, a stacked bar)? Only when no branch filter is applied, or when
 * both respond to it.
 *
 * A branch-scoped numerator over a tenant-wide denominator is not a ratio of
 * anything, and it fails in a specific, alarming direction: filtering shrinks
 * sales while tenant-wide commissions stay put, so a mid-size branch renders
 * "312% of sales went to commissions" and a NEGATIVE "retained" in credit
 * green. Suppressing the derived line (and un-stacking the chart) is the honest
 * move — both underlying figures stay on screen with their own labels and their
 * own scope badges.
 */
export function scopesComparable(
  a: KpiCardData | undefined,
  b: KpiCardData | undefined,
  filtered = false,
): boolean {
  if (!a || !b) return false;
  if (!filtered) return true;
  return a.scope === b.scope;
}
